import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../supabase/client';
import type { AuthError, User } from '@supabase/supabase-js';
import type { SignUpInput, SignInInput, ResetPasswordInput, UpdatePasswordInput } from './validation';

const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
const MAX_ATTEMPTS = 3;

interface RateLimit {
  attempts: number;
  lastAttempt: number;
}

const rateLimits: Record<string, RateLimit> = {};

function checkRateLimit(email: string): boolean {
  const now = Date.now();
  const limit = rateLimits[email];

  if (!limit) {
    rateLimits[email] = { attempts: 1, lastAttempt: now };
    return true;
  }

  if (now - limit.lastAttempt > RATE_LIMIT_WINDOW) {
    rateLimits[email] = { attempts: 1, lastAttempt: now };
    return true;
  }

  if (limit.attempts >= MAX_ATTEMPTS) {
    const waitTime = Math.ceil((RATE_LIMIT_WINDOW - (now - limit.lastAttempt)) / 1000);
    throw new Error(`Too many attempts. Please try again in ${waitTime} seconds.`);
  }

  limit.attempts += 1;
  limit.lastAttempt = now;
  return true;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize user state from any existing session
    const initSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };
    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('Auth state change:', _event);
      
      // Update user state without navigation
      setUser(session?.user ?? null);
      
      // Only navigate on explicit sign in/out events
      if (_event === 'SIGNED_IN') {
        // Handle profile update in the background
        if (session?.user?.email_confirmed_at) {
          const profileData = {
            id: session.user.id,
            email: session.user.email,
            full_name: session.user.user_metadata.full_name || 'Unknown',
            user_type: 'user'
          };

          // Fire and forget profile update
          supabase
            .from('profiles')
            .upsert(profileData, { onConflict: 'id' })
            .then(({ error }) => {
              if (error) {
                console.error('Profile update error:', error);
              }
            });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthError = (error: AuthError) => {
    console.error('Auth error:', error);
    let message = error.message;
    
    if (error.message.includes('rate limit')) {
      message = 'Too many attempts. Please wait a few minutes before trying again.';
    } else if (error.message.includes('Invalid login credentials')) {
      message = 'Invalid email or password.';
    }

    setError(message);
    toast.error(message);
    throw error;
  };

  const signUp = async ({ email, password, name }: SignUpInput) => {
    try {
      console.log('Starting signup process for:', email);
      setLoading(true);
      setError(null);

      checkRateLimit(email);

      const siteUrl = import.meta.env.VITE_APP_URL;
      console.log('Using site URL:', siteUrl);
      console.log('Redirect URL:', `${siteUrl}/auth/callback`);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
          emailRedirectTo: `${siteUrl}/auth/callback`,
        },
      });

      console.log('Signup response:', { 
        data: {
          ...data,
          user: {
            ...data?.user,
            identities: data?.user?.identities,
            confirmed_at: data?.user?.confirmed_at,
            email_confirmed_at: data?.user?.email_confirmed_at,
            last_sign_in_at: data?.user?.last_sign_in_at,
          }
        }, 
        error 
      });

      if (error) throw error;

      if (!data?.user?.email_confirmed_at) {
        toast.success('Please check your email to verify your account.');
        console.log('Verification email should be sent');
      } else {
        toast.success('Account created and email already verified!');
        console.log('Email already verified');
      }

      return data;
    } catch (error: any) {
      console.error('Signup error:', error);
      const message = error.message || 'An error occurred during signup';
      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async ({ email, password }: SignInInput) => {
    try {
      const startTime = performance.now();
      console.log('Starting signin process:', performance.now());
      setLoading(true);
      setError(null);

      checkRateLimit(email);

      console.log('Before Supabase auth call:', performance.now());
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log('After Supabase auth call:', performance.now());

      console.log('Signin response timing:', { 
        data, 
        error,
        totalTime: performance.now() - startTime 
      });

      if (error) throw error;
      
      toast.success('Welcome back!');
      console.log('Signin completed, total time:', performance.now() - startTime);
      return data;
    } catch (err) {
      console.error('Signin error:', err);
      handleAuthError(err as AuthError);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('Starting signout process');
      setLoading(true);
      setError(null);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast.success('Signed out successfully');
      navigate('/');
      console.log('Signout completed successfully');
    } catch (err) {
      console.error('Signout error:', err);
      handleAuthError(err as AuthError);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async ({ email }: ResetPasswordInput) => {
    try {
      console.log('Starting password reset for:', email);
      setLoading(true);
      setError(null);

      checkRateLimit(email);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${import.meta.env.VITE_APP_URL}/auth/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset instructions have been sent to your email');
      console.log('Password reset email sent successfully');
    } catch (err) {
      console.error('Password reset error:', err);
      handleAuthError(err as AuthError);
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async ({ password }: UpdatePasswordInput) => {
    try {
      console.log('Starting password update');
      setLoading(true);
      setError(null);

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast.success('Password has been updated successfully');
      navigate('/');
      console.log('Password updated successfully');
    } catch (err) {
      console.error('Password update error:', err);
      handleAuthError(err as AuthError);
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };
}